import { useParams } from 'react-router-dom'
import useLoadSurveyContext from '../../../hooks/useLoadSurveyContext'
import HeadlineSplit from '../../../UI/Headline/Split'
import { useSelector } from 'react-redux'
import props from '../../../../redux/props'
import LargeContainer from '../../../UI/Container/LargeContainer'
import useLoadCustomer from '../../../hooks/useLoadCustomer'
import useLoadProject from '../../../hooks/useLoadProject'
import Rebuild from './Rebuild/Rebuild'
import ManageConcepts from './ManageConcepts/ManageConcepts'
import ConceptCalculation from './ConceptCalculation/ConceptCalculation'
import Optimization from './Optimization/Optimization'
import Graph from './Graph/Graph'
import Info from './Info/Info'
import useConceptConfiguration from './ConceptCalculation/useConceptConfiguration'
import Loading from '../../../UI/Loading/Loading'
import Input from '../../../UI/Input/Input'
import _ from 'lodash'
import Summary from './Summary/Summary'
import SummaryProductionDistance from './Summary/SummaryProductionDistance'
import SummaryFloor from './Summary/SummaryFloor'
import TeamElementList from './ElementList/TeamElementList'
import ManagerElementList from './ElementList/ManagerElementList'
import LeaderElementList from './ElementList/LeaderElementList'
import { SurveyType } from '../../../../types/Survey/Survey'
import Colored from '../../../UI/Headline/Colored'
import { useState } from 'react'
import Dropdown from '../../../UI/Dropdown/Dropdown'
import { ProductionDistance } from '../../../../types/OrganizationUnit'
import Accordion from '../../../UI/Accordion/Accordion'
import ActiveConceptHeader from './ActiveConceptHeader/ActiveConceptHeader'
import Button from '../../../UI/Button/Button'
import Form from '../../../UI/Form/Form'
import ToggleScreenshotMode from './ToggleScreenshotMode/ToggleScreenshotMode'

enum ShowPlainResultsOf {
  TEAM = 'TEAM',
  MANAGER = 'MANAGER',
  LEADER = 'LEADER',
}

const Analysis = () => {
  const t = useSelector((s) => s[props.TRANSLATION])
  const { surveyContextUUID } = useParams()
  const [context] = useLoadSurveyContext(surveyContextUUID)
  const project = useLoadProject(context?.projectUUID)
  const customer = useLoadCustomer(project?.customerUUID)
  const { conceptDataCluster, loading, getActiveConcept, activeConceptUUID, updateActiveConcept } = useConceptConfiguration(surveyContextUUID)
  const [showPlainResultsOf, setShowPlainResultsOf] = useState(ShowPlainResultsOf.TEAM)
  const [conceptName, setConceptName] = useState(getActiveConcept()?.name)

  const updateConceptName = () => {
    const conceptCopy = _.cloneDeep(getActiveConcept())
    conceptCopy.name = conceptName
    updateActiveConcept(conceptCopy)
  }

  const getSummaryItems = () => {
    const base = [
      {
        key: 'summary',
        title: t.survey.analysis.concept.summary.general.generalTitle,
        content: <Summary />,
      },
      {
        key: 'summary-production-distance-fern',
        title: t.survey.analysis.concept.summary.productionDistance.fern.title,
        content: <SummaryProductionDistance productionDistance={ProductionDistance.FERN} />,
      },
      {
        key: 'summary-production-distance-nah',
        title: t.survey.analysis.concept.summary.productionDistance.nah.title,
        content: <SummaryProductionDistance productionDistance={ProductionDistance.NAH} />,
      },
    ]

    const floorItems = []
    if (context.floors.length > 0) {
      context.floors.forEach((floor) => {
        floorItems.push({
          key: `summary-floor-${floor.name}`,
          title: t.survey.analysis.concept.summary.floorSummary.title(floor.name),
          content: <SummaryFloor floorUUID={floor.uuid} />,
        })
      })
    }

    return [...base, ...floorItems]
  }

  if (!context || !project || !customer) return <></>
  // const hasAnswerCountByLeader = context.answerCountByLeader > 0
  // const hasAnswerCountByManager = context.answerCountByManager > 0
  const hasAnswerCountByOrganizationUnit = context.answerCountByOrganizationUnit.reduce((acc, curr) => acc + curr.answerCount, 0) > 0
  // const hasSum = getActiveConcept()?.units.sum.total === 0
  const isEmpty = !hasAnswerCountByOrganizationUnit
  return (
    <div>
      <ActiveConceptHeader activeConcept={getActiveConcept()} />
      <LargeContainer>
        <div className="flex flex-col gap-1 pt-2 pb-10 mt-10">
          <div className="flex items-start justify-between">
            <HeadlineSplit
              prefix={t.survey.analysis.titleSplit.prefix}
              suffix={t.survey.analysis.titleSplit.suffix}
              dynamicSuffix={context.departmentName}
              subline={t.survey.analysis.titleSplit.subline(customer.name, project.name)}
            />
            <Rebuild uuid={context.surveyList.find((s) => s.surveyType === SurveyType.TEAM).uuid} />
          </div>
          <Loading loading={!loading} />
          {!loading && !!isEmpty && (
            <>
              {!hasAnswerCountByOrganizationUnit && (
                <div className="text-gray text-center mt-10">{t.survey.analysis.calculation.noData.organizationUnit}</div>
              )}
              {/* {hasAnswerCountByOrganizationUnit && !hasSum && (
              <div className="text-gray text-center mt-10">{t.survey.analysis.calculation.noDataAvailable}</div>
            )} */}
            </>
          )}
          {!loading && !isEmpty && (
            <div className="flex flex-col md:flex-row mt-10 min-h-screen">
              <div className="w-2/3 flex flex-col gap-10">
                <Info />
                {!!!!activeConceptUUID && (
                  <>
                    <Graph />
                    {getActiveConcept() && (
                      <Form className="font-bold text-lg p-2 mt-5 flex gap-3 items-center" onSubmit={updateConceptName}>
                        <Colored className="whitespace-nowrap mt-1" text={t.survey.analysis.concept.activeConcept.prefix} />
                        <Input required className="!w-fit" contentClassName="!w-fit py-0" value={conceptName} onChange={setConceptName} />
                        <Button type="submit" text={t.generic.confirm} />
                      </Form>
                    )}
                    <div className="shadow-lg p-5">
                      <Accordion invert items={getSummaryItems()} />
                    </div>
                    <Optimization />
                    <ConceptCalculation />
                  </>
                )}
              </div>
              <div className="min-h-[100%] border-r-2 border-blue ml-5 mr-10"></div>
              <div className="w-1/3 flex flex-col gap-10">
                <ManageConcepts />
                <ToggleScreenshotMode />
                <div>
                  <Colored text={t.survey.analysis.averageValues.headline} />
                  <Dropdown
                    label={t.survey.analysis.averageValues.filter.showPlainResultsOf.label}
                    items={[
                      { label: t.survey.analysis.averageValues.filter.showPlainResultsOf.TEAM, value: ShowPlainResultsOf.TEAM },
                      { label: t.survey.analysis.averageValues.filter.showPlainResultsOf.MANAGER, value: ShowPlainResultsOf.MANAGER },
                      { label: t.survey.analysis.averageValues.filter.showPlainResultsOf.LEADER, value: ShowPlainResultsOf.LEADER },
                    ]}
                    value={showPlainResultsOf}
                    onChange={setShowPlainResultsOf}
                    className="mb-5"
                  />
                  {showPlainResultsOf === ShowPlainResultsOf.TEAM && <TeamElementList survey={conceptDataCluster.teamSurvey} />}
                  {showPlainResultsOf === ShowPlainResultsOf.MANAGER && (
                    <ManagerElementList survey={conceptDataCluster.managerSurvey} allAnswers={conceptDataCluster.allManagerAnswers} />
                  )}
                  {showPlainResultsOf === ShowPlainResultsOf.LEADER && (
                    <LeaderElementList survey={conceptDataCluster.leaderSurvey} answer={conceptDataCluster.leaderAnswer} />
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </LargeContainer>
    </div>
  )
}

export default Analysis
